import { call, put, take, race, delay } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import { getCurrentToken, refreshAuth } from '../../features/account/login/login.api';
import { SessionInfoDto, TokenDto } from '../../features/account/login/login.dtos';
import { initializeAuthActions, userLoggedIn } from '../actions/auth.actions';
import { logoutActions } from '../../features/account/login/login.actions';
import { AuthDto, authInit } from '../dtos/auth';
import {
  storageRoles,
  storageAuthInformation,
} from '../constants/strings';

function* refreshTokenLoop(expiresInMs: number) {
  const maxRetries = 3;
  const retryDelays = [10000, 30000, 60000];

  while (true) {
    const refreshDelay = Math.max(expiresInMs * 0.75, 10000);
    yield delay(refreshDelay);

    let succeeded = false;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const newToken: TokenDto = yield call(refreshAuth);
        if (newToken && newToken.expiresIn > 0) {
          expiresInMs = newToken.expiresIn;
        }
        succeeded = true;
        break;
      } catch (error) {
        if (attempt < maxRetries - 1) {
          yield delay(retryDelays[attempt]);
        }
      }
    }

    if (!succeeded) {
      yield put({ type: getType(logoutActions.request) });
      return;
    }
  }
}

function* hydrateAndStartRefreshLoop(sessionInfo: SessionInfoDto) {
  const auth: AuthDto = {
    status: 'authenticated',
    authToken: '',
    refreshToken: '',
    isAdmin: Boolean(sessionInfo.roles.find((r: string) => r === 'admin')),
    isAuthenticated: true,
    isPasswordExpired: sessionInfo.isPasswordExpired,
    roles: sessionInfo.roles,
    fullName: sessionInfo.fullName,
    employerId: sessionInfo.employerId ?? '',
    contactId: sessionInfo.contactId ?? '',
    employers: sessionInfo.employers.map(e => e.toString()),
  };

  sessionStorage.setItem(storageRoles, JSON.stringify(sessionInfo.roles));
  sessionStorage.setItem(storageAuthInformation, JSON.stringify({
    userId: sessionInfo.userId,
    userName: sessionInfo.userName,
    fullName: sessionInfo.fullName,
    roles: sessionInfo.roles,
    employers: sessionInfo.employers,
    contactId: sessionInfo.contactId,
    employerId: sessionInfo.employerId,
    tenant: sessionInfo.tenant,
    isPasswordExpired: sessionInfo.isPasswordExpired,
  }));

  yield put({ type: getType(userLoggedIn), payload: auth });
  yield put({ type: getType(initializeAuthActions.success), payload: sessionInfo });

  if (sessionInfo.expiresIn > 0) {
    yield call(refreshTokenLoop, sessionInfo.expiresIn);
  }
}

/**
 * Initializes auth state by calling /current-token endpoint,
 * then enters a refresh loop to keep the JWT alive.
 *
 * If the JWT has expired (e.g. laptop sleep > 30 min), falls back to
 * /refresh-token which uses the longer-lived HttpOnly refresh token cookie.
 * This covers both Duo and non-Duo users after any page reload.
 */
function* initializeAuth() {
  try {
    let sessionInfo: SessionInfoDto | null = null;

    try {
      sessionInfo = yield call(getCurrentToken);
    } catch (tokenError) {
      // JWT may be expired but the refresh token cookie (540 min) may still be valid.
      // refreshAuth is [AllowAnonymous] + [IgnoreAntiforgeryToken] so it works without a valid JWT.
      yield call(refreshAuth);
      sessionInfo = yield call(getCurrentToken);
    }

    if (sessionInfo && sessionInfo.userId && sessionInfo.roles && sessionInfo.roles.length > 0) {
      yield call(hydrateAndStartRefreshLoop, sessionInfo);
    } else {
      const anonymousAuth: AuthDto = {
        ...authInit,
        status: 'anonymous',
      };
      yield put({ type: getType(userLoggedIn), payload: anonymousAuth });
      yield put({ type: getType(initializeAuthActions.failure), payload: 'No valid session' });
    }
  } catch (error) {
    sessionStorage.removeItem(storageRoles);
    sessionStorage.removeItem(storageAuthInformation);
    const anonymousAuth: AuthDto = {
      ...authInit,
      status: 'anonymous',
    };
    yield put({ type: getType(userLoggedIn), payload: anonymousAuth });
    yield put({ type: getType(initializeAuthActions.failure), payload: 'Unauthorized' });
  }
}

export function* watchInitializeAuth() {
  while (true) {
    yield take(getType(initializeAuthActions.request));
    yield race({
      auth: call(initializeAuth),
      logout: take(getType(logoutActions.request)),
    });
  }
}
